// Everything that can go wrong on the way to placing an order, with the message the customer should
// see and whether sending again can help. Kept free of Firebase/DOM so it is easy to test.

/** The App Check token could not be obtained (reCAPTCHA blocked, offline, or it took too long). */
export class AppCheckUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AppCheckUnavailableError';
  }
}

/** Rejects with `timeoutError()` if `promise` has not settled within `ms`. */
export function withTimeout<T>(promise: Promise<T>, ms: number, timeoutError: () => Error): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => { timer = setTimeout(() => reject(timeoutError()), ms); }),
  ]).finally(() => { if (timer) clearTimeout(timer); });
}

export type OrderErrorKind = 'appcheck' | 'busy' | 'timeout' | 'network' | 'server';

export class OrderUploadError extends Error {
  constructor(
    message: string,
    public readonly kind: OrderErrorKind,
    /** Whether sending the same order again can succeed (a 401 or a rejected file cannot). */
    public readonly retryable: boolean,
    /** What to show the customer. */
    public readonly userMessage: string,
    /** How long to wait before the retry. */
    public readonly retryAfterMs = 0,
  ) {
    super(message);
    this.name = 'OrderUploadError';
  }
}

export const ORDER_MESSAGES: Record<OrderErrorKind, string> = {
  appcheck: 'Nepodařilo se ověřit, že objednávku odesíláte vy (Google reCAPTCHA). Zkontrolujte, zda ji neblokuje blokátor reklam, VPN nebo firemní síť, případně zkuste jiný prohlížeč. Pak stránku obnovte a objednávku odešlete znovu.',
  busy: 'Objednávka se právě zpracovává. Chvíli počkejte a zkuste to znovu, stejná objednávka se neuloží dvakrát.',
  timeout: 'Server neodpověděl včas. Zkuste odeslání zopakovat, stejná objednávka se neuloží dvakrát.',
  network: 'Nepodařilo se spojit se serverem. Zkontrolujte připojení k internetu a zkuste to znovu.',
  server: 'Soubor se nepodařilo uložit. Zkuste objednávku odeslat znovu.',
};

/** Error for a non-2xx answer of /api/upload-order. */
export function orderErrorFromResponse(status: number, retryAfterHeader: string | null, detail = ''): OrderUploadError {
  const message = `Drive upload selhal: ${status} ${detail}`.trim();
  if (status === 401) {
    return new OrderUploadError(message, 'appcheck', false, ORDER_MESSAGES.appcheck);
  }
  // Our own "the same order is still being processed" answer carries Retry-After; a bare 503 is
  // Cloud Run reporting an overloaded or restarting instance.
  const retryAfterSeconds = Number(retryAfterHeader);
  if (status === 503 && retryAfterHeader && Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
    return new OrderUploadError(message, 'busy', true, ORDER_MESSAGES.busy, Math.min(retryAfterSeconds, 15) * 1000);
  }
  if (status === 400 || status === 413) {
    return new OrderUploadError(message, 'server', false, ORDER_MESSAGES.server);
  }
  return new OrderUploadError(message, 'server', true, ORDER_MESSAGES.server, 2000);
}

/** Error for a failure that never produced an answer (token, timeout, network). */
export function orderErrorFromException(err: unknown, timeoutMs: number): OrderUploadError {
  if (err instanceof OrderUploadError) return err;
  if (err instanceof AppCheckUnavailableError) {
    return new OrderUploadError(err.message, 'appcheck', false, ORDER_MESSAGES.appcheck);
  }
  if ((err as { name?: string } | null)?.name === 'AbortError') {
    return new OrderUploadError(`Drive upload selhal: vypršel časový limit (${Math.round(timeoutMs / 1000)} s).`, 'timeout', true, ORDER_MESSAGES.timeout, 2000);
  }
  return new OrderUploadError(`Drive upload selhal: ${(err as Error)?.message ?? String(err)}`, 'network', true, ORDER_MESSAGES.network, 2000);
}
